<script setup lang="ts">
import { ActionButton, FileUpload, StatusTag } from '@taler/ui'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { RestoreBackupInput } from '../composables/use-backup-restore.ts'

const props = defineProps<{
  isRestoring: boolean
  errorDetail: string | undefined
}>()
const emit = defineEmits<{ submit: [input: RestoreBackupInput] }>()
const { t } = useI18n()

const ledgerName = ref('')
const file = ref<File | null>(null)

const canSubmit = computed(() => ledgerName.value.trim().length > 0 && file.value !== null)

function onSubmit(): void {
  if (!canSubmit.value || file.value === null) return
  emit('submit', { ledgerName: ledgerName.value.trim(), file: file.value })
}
</script>

<template>
  <form
    class="flex flex-col gap-[var(--space-group)] rounded-[var(--radius-surface)] border border-[color:var(--color-border)] p-[var(--space-block)] bg-[color:var(--color-surface)]"
    @submit.prevent="onSubmit"
  >
    <label
      class="flex flex-col gap-[var(--space-hair)] text-[length:var(--text-step-2)] text-[color:var(--color-text)]"
    >
      {{ t('backupRestore.ledgerName') }}
      <input
        v-model="ledgerName"
        type="text"
        :placeholder="t('backupRestore.ledgerNamePlaceholder')"
        class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-[var(--space-cell)] py-[var(--space-inline)] text-[color:var(--color-text)]"
      />
    </label>

    <FileUpload
      v-model="file"
      :label="t('backupRestore.fileLabel')"
      accept="application/json,.json"
      :accept-hint="t('backupRestore.fileAcceptHint')"
      :browse-label="t('backupRestore.fileBrowse')"
      :drop-hint="t('backupRestore.fileDropHint')"
      :remove-label="t('backupRestore.fileRemove')"
      :invalid-type-message="t('backupRestore.fileInvalidType')"
      :preview="false"
    />

    <ActionButton type="submit" :disabled="props.isRestoring || !canSubmit">
      {{ props.isRestoring ? t('backupRestore.restoring') : t('backupRestore.restore') }}
    </ActionButton>

    <StatusTag v-if="props.errorDetail" tone="danger">{{ props.errorDetail }}</StatusTag>
  </form>
</template>
